import { PropOption } from "../../PropX";

export type Suggestions = {
  [key: string]: true | Suggestions;
};

const isSuggestionSymbol = Symbol.for("suggestion");

const walk = (suggestions: Suggestions | true | void, keyPath: string[], lookBack: string[]): string[] => {
  if (suggestions === undefined) {
    throw new Error(`Invalid key path: ${lookBack.join(".")}`);
  }

  if (suggestions === true) {
    return [];
  }

  if (keyPath.length === 0) {
    return Object.keys(suggestions);
  }

  /**
   * When one key remains, we are at the end of the key path and we should use
   * the last key as a search key. If the search key finds an exact match, then
   * throw away the rest of the paths and display the next set of options.
   */
  if (keyPath.length === 1) {
    const [match] = keyPath;

    const options: string[] = [];

    for (const value in suggestions) {
      if (value === match) {
        return walk(suggestions[value], [], [...lookBack, value]);
      }

      if (value.includes(match)) {
        options.push(value);
      }
    }

    return options;
  }

  const [head, ...tail] = keyPath;

  return walk(suggestions[head], tail, [...lookBack, head]);
};

export const findSuggestions = (suggestions: Suggestions | true, keyPath: string[]): string[] => {
  return walk(suggestions, keyPath, []);
};

export const suggestionsToOptions = (suggestions: Suggestions | true, keyPath: string[] = []): PropOption[] => {
  return findSuggestions(suggestions, keyPath).map((value) => ({ value }));
};

export const isValidSuggestion = (suggestions: Suggestions | true): boolean => {
  return suggestions === true || isSuggestionSymbol in suggestions;
};
